import {Box} from "@mui/material";
import {forwardRef} from "react";
import QRCodeSVG from "react-qr-code";
import {QR_LOGO_MAX_RATIO, QR_LOGO_PAD_RATIO, QR_LOGO_RADIUS_RATIO, qrLogoLength} from "./surveyQrBranding.util";

interface Props {
    /** The link the code encodes - it is never rendered as text. */
    value: string
    /**
     * Edge length of the code. A number is a pixel size; a CSS length (e.g. 'min(70vw, 70vh)')
     * lets the code scale with the viewport instead - the SVG carries a viewBox, so the style
     * simply overrides the width/height the renderer writes.
     */
    size?: number | string
    padding?: number
    /** Brand mark laid over the centre of the code; resolved by `useSurveyQrLogoUrl`. */
    logoUrl?: string
}

/** Only used when the caller sizes the code with CSS - the viewBox makes the number irrelevant. */
const RESPONSIVE_RENDER_SIZE = 1024

/**
 * The scannable code itself, always on white: a camera has to read it in dark mode too.
 * Kept separate from the fullscreen handover that renders it so the markup stays reusable - the
 * PNG download reads the SVG straight out of this wrapper through the forwarded ref.
 *
 * A logo, when the account has one, sits over the centre as a DOM overlay rather than inside the
 * SVG - which is why {@link downloadSurveyQrCode} has to re-draw it onto the exported canvas. The
 * white pad under it is not decoration: it guarantees contrast whatever the logo's own background
 * or transparency, so the reader still sees a clean quiet zone around the mark. Error correction
 * stays at level 'H', the highest, which is what makes covering the centre safe in the first place.
 */
const SurveyQrCode = forwardRef<HTMLDivElement, Props>(({value, size = 200, padding = 2.5, logoUrl}, ref) => {

    const renderSize = typeof size === 'number' ? size : RESPONSIVE_RENDER_SIZE
    const responsiveStyle = typeof size === 'string'
        ? {width: size, height: size, maxWidth: '100%'}
        : undefined

    return (
        <Box
            ref={ref}
            sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                p: padding,
                backgroundColor: 'common.white',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
            }}
        >
            <QRCodeSVG value={value} size={renderSize} level={'H'} style={responsiveStyle}/>

            {/* Centred on the wrapper, which is the code plus symmetric padding - so it lands dead
                centre on the code itself. The pad hugs the logo's own aspect instead of forcing a
                square, which keeps a wide wordmark from blanking more modules than it needs. */}
            {!!logoUrl && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        p: qrLogoLength(size, QR_LOGO_MAX_RATIO * QR_LOGO_PAD_RATIO),
                        backgroundColor: 'common.white',
                        borderRadius: qrLogoLength(size, QR_LOGO_MAX_RATIO * QR_LOGO_RADIUS_RATIO),
                    }}
                >
                    <Box
                        component={'img'}
                        src={logoUrl}
                        alt={''}
                        sx={{
                            display: 'block',
                            maxWidth: qrLogoLength(size, QR_LOGO_MAX_RATIO),
                            maxHeight: qrLogoLength(size, QR_LOGO_MAX_RATIO),
                            objectFit: 'contain',
                        }}
                    />
                </Box>
            )}
        </Box>
    )
})

SurveyQrCode.displayName = 'SurveyQrCode'

export default SurveyQrCode
